/**
 * API Client for oRPC REST API v2
 *
 * This client provides type-safe HTTP methods for communicating with the new API v2.
 * It handles authentication, error handling, and request/response transformation.
 *
 * Usage:
 * ```typescript
 * import { api } from '@/api/v2/client'
 *
 * // GET request
 * const entities = await api.get<EntityListResponse>('/entities')
 *
 * // POST request
 * const newEntity = await api.post<Entity>('/entities', { name: { en: 'Test' } })
 *
 * // PATCH request
 * const updated = await api.patch<Entity>('/entities/123', { name: { en: 'Updated' } })
 *
 * // DELETE request
 * await api.delete('/entities/123')
 * ```
 */
import { useUserStore } from '@/stores/user'
import { getSessionId } from '@/utils/session'

/**
 * API v2 Base URL
 * Reads from environment variable with fallback
 */
export const API_BASE = import.meta.env.VITE_APP_PUBLIC_API_HTTP_URL + '/api/v2'

/**
 * Standard API error response structure
 */
export interface ApiError {
  error: {
    code: string
    message: string
    details?: Record<string, unknown>
    requestId?: string
  }
}

/**
 * Custom error class for API errors
 */
export class ApiClientError extends Error {
  public readonly code: string
  public readonly details?: Record<string, unknown>
  public readonly requestId?: string
  public readonly status: number

  constructor(error: ApiError['error'], status: number) {
    super(error.message)
    this.name = 'ApiClientError'
    this.code = error.code
    this.details = error.details
    this.requestId = error.requestId
    this.status = status
  }
}

/**
 * Base API client function
 * Handles authentication, headers, and error responses
 */
export async function apiClient<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const userStore = useUserStore()

  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE}${endpoint}`

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Correlation-ID': crypto.randomUUID(),
      'X-Session-ID': getSessionId(),
      ...(userStore.token && { Authorization: `Bearer ${userStore.token}` }),
      ...options.headers,
    },
  })

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T
  }

  // Handle error responses
  if (!response.ok) {
    let errorBody: ApiError

    try {
      errorBody = await response.json()
    } catch {
      // If we can't parse the error, create a generic one
      errorBody = {
        error: {
          code: 'UNKNOWN_ERROR',
          message: response.statusText || 'An unknown error occurred',
        },
      }
    }

    throw new ApiClientError(errorBody.error, response.status)
  }

  // Parse successful response
  return response.json()
}

/**
 * Build query string from parameters object
 * Handles nested objects with bracket notation
 */
export function buildQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams()

  const appendParams = (obj: Record<string, unknown>, prefix = '') => {
    for (const [key, value] of Object.entries(obj)) {
      if (value === undefined || value === null) continue

      const paramKey = prefix ? `${prefix}[${key}]` : key

      if (typeof value === 'object' && !Array.isArray(value)) {
        appendParams(value as Record<string, unknown>, paramKey)
      } else if (Array.isArray(value)) {
        value.forEach(v => searchParams.append(`${paramKey}[]`, String(v)))
      } else {
        searchParams.set(paramKey, String(value))
      }
    }
  }

  appendParams(params)
  return searchParams.toString()
}

/**
 * Convenience methods for common HTTP operations
 */
export const api = {
  /**
   * GET request
   * @param endpoint - API endpoint (e.g., '/entities')
   * @param params - Optional query parameters
   */
  get: <T>(endpoint: string, params?: Record<string, unknown>): Promise<T> => {
    const query = params ? buildQueryString(params) : ''
    const url = query ? `${endpoint}?${query}` : endpoint
    return apiClient<T>(url, { method: 'GET' })
  },

  /**
   * POST request
   * @param endpoint - API endpoint
   * @param data - Request body
   */
  post: <T>(endpoint: string, data?: unknown): Promise<T> =>
    apiClient<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),

  /**
   * PATCH request
   * @param endpoint - API endpoint
   * @param data - Request body
   */
  patch: <T>(endpoint: string, data: unknown): Promise<T> =>
    apiClient<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  /**
   * PUT request
   * @param endpoint - API endpoint
   * @param data - Request body
   */
  put: <T>(endpoint: string, data: unknown): Promise<T> =>
    apiClient<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  /**
   * DELETE request
   * @param endpoint - API endpoint
   * @param params - Optional query parameters
   */
  delete: <T>(endpoint: string, params?: Record<string, unknown>): Promise<T> => {
    const query = params ? buildQueryString(params) : ''
    const url = query ? `${endpoint}?${query}` : endpoint
    return apiClient<T>(url, { method: 'DELETE' })
  },
}

export default api
